import { useEffect, useEffectEvent, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { DeleteFlowModal, FlowEmailTimeline, FlowEmptyState, FlowRow } from '..';
import { useEmailFlows } from '../../queries';
import { useFlowActions } from '../../hooks';
import { FLOW_HEADER_COLUMNS } from '../../constants/gridColumns';
import IcPlus from '@/assets/icons/ic-plus.svg';
import {
    StanAccordion,
    StanAccordionBody,
    StanAccordionHeader,
    StanAccordionItem,
    StanDataRowGroup,
} from '@/components';
import { useAccordionController } from '@/hooks/useAccordionController';
import { FlowsTabSkeleton } from './FlowsTabSkeleton';

interface FlowsTabProps {
    isEmailFlowsAllowed: boolean;
    isUserLoading: boolean;
}

export const FlowsTab = ({ isEmailFlowsAllowed, isUserLoading }: FlowsTabProps) => {
    const { flows, total, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useEmailFlows();
    const { openDeleteModal, closeDeleteModal, confirmDelete, deleteModalState } = useFlowActions();
    const firstLoadDoneRef = useRef(false);
    const prevFirstFlowIdRef = useRef<number | null>(null);
    const [searchParams] = useSearchParams();
    const openFlowId = searchParams.get('open');

    // Initialize accordion with open flow if specified in URL
    const accordionController = useAccordionController({
        allowMultipleOpen: false,
        initialOpenItems: openFlowId ? [`flow-${openFlowId}`] : [],
    });

    const handleScrollToFlow = useEffectEvent((flowId: string) => {
        requestAnimationFrame(() => {
            document.getElementById(flowId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    });

    // Scroll to flow specified in URL query parameter
    useEffect(() => {
        if (!openFlowId || isLoading) return;

        const flowId = `flow-${openFlowId}`;
        const flowExists = flows.some(flow => flow.flow_id.toString() === openFlowId);

        if (flowExists) {
            handleScrollToFlow(flowId);
        }
    }, [openFlowId, isLoading, flows]);

    const handleNewFlowDetection = useEffectEvent((flowList: typeof flows) => {
        const firstFlow = flowList[0];
        const currentFirstFlowId = firstFlow?.flow_id ?? null;

        if (!firstLoadDoneRef.current) {
            firstLoadDoneRef.current = true;
            prevFirstFlowIdRef.current = currentFirstFlowId;
            return;
        }

        // Only scroll if the FIRST flow changed (new flow created/duplicated)
        const prevFirstFlowId = prevFirstFlowIdRef.current;
        if (currentFirstFlowId !== null && currentFirstFlowId !== prevFirstFlowId) {
            const flowId = `flow-${currentFirstFlowId}`;
            accordionController.openItem(flowId);
            handleScrollToFlow(flowId);
        }

        prevFirstFlowIdRef.current = currentFirstFlowId;
    });

    // Detect when a new flow is added (e.g., from duplication)
    useEffect(() => {
        if (isLoading) return;
        handleNewFlowDetection(flows);
    }, [flows, isLoading]);

    // Show loading state while user data or flows are loading
    if (isUserLoading || isLoading) {
        return <FlowsTabSkeleton />;
    }

    // Show upgrade empty state if not allowed
    if (!isEmailFlowsAllowed) {
        return <FlowEmptyState variant='upgrade' />;
    }

    // Show add-flow empty state if no flows exist
    if (flows.length === 0) {
        return <FlowEmptyState variant='add-flow' icon={<IcPlus />} />;
    }

    return (
        <div className='space-y-6'>
            <StanDataRowGroup headerColumns={[...FLOW_HEADER_COLUMNS]} maxActionButtonCount={2}>
                <StanAccordion controller={accordionController}>
                    <AnimatePresence initial={false}>
                        {flows.map((flow, index) => {
                            const isFirst = index === 0;

                            return (
                                <motion.div
                                    key={flow.flow_id}
                                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                    animate={{
                                        opacity: 1,
                                        height: 'auto',
                                        marginTop: isFirst ? 0 : 24,
                                    }}
                                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                    transition={{
                                        duration: 0.4,
                                        ease: 'backOut',
                                    }}
                                >
                                    <StanAccordionItem
                                        id={`flow-${flow.flow_id}`}
                                        className='bg-bg-canvas shadow-stan!'
                                    >
                                        <StanAccordionHeader asChild className='max-md:px-4!'>
                                            <FlowRow flow={flow} onDelete={openDeleteModal} />
                                        </StanAccordionHeader>
                                        <StanAccordionBody>
                                            <FlowEmailTimeline
                                                flowEmails={flow.flow_emails || []}
                                                flowId={flow.flow_id}
                                                flowStatus={flow.status}
                                                flowTrigger={flow.trigger || flow.trigger_type || 'checkout'}
                                                flow={flow}
                                                hasNewReferralTag={flow.data?.specific_time?.tags?.some(
                                                    tag => tag.name === 'new-referral'
                                                )}
                                            />
                                        </StanAccordionBody>
                                    </StanAccordionItem>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </StanAccordion>
            </StanDataRowGroup>

            {/* Load more pagination */}
            {hasNextPage && (
                <button
                    type='button'
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className='text-label-sm-regular text-secondary hover:text-dark cursor-pointer p-2.5 transition-colors disabled:opacity-50'
                >
                    {isFetchingNextPage ? (
                        'Loading...'
                    ) : (
                        <>
                            Displaying {flows.length} of {total} · Load more ↓
                        </>
                    )}
                </button>
            )}

            <DeleteFlowModal
                isOpen={deleteModalState.isOpen}
                flowName={deleteModalState.flowName}
                onConfirm={confirmDelete}
                onCancel={closeDeleteModal}
            />
        </div>
    );
};
